import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, StatusPill, ProgressBar, cn, formatCurrency } from '@careandkey/shared';
import { supabase } from '../lib/supabase';
import { useOrg } from '../lib/org';
import { Layout, Topbar } from '../components/Layout';
import { fmtDate } from '../lib/fmt';
import { ContractForm, type ContractRow } from '../components/ContractForms';
import { AllowanceEditor } from '../components/AllowanceEditor';

type Contract = ContractRow & {
  contract_number: string | null; visits_consumed: number;
  clients: { name: string } | null; properties: { name: string } | null;
  contract_properties: { properties: { name: string } | null }[];
};
const TONE: Record<string, 'open' | 'inprogress' | 'done' | 'overdue' | 'onhold'> = { draft: 'open', active: 'done', expired: 'onhold', cancelled: 'overdue' };

export function Contracts() {
  const { t, i18n } = useTranslation();
  const { timezone } = useOrg();
  const [rows, setRows] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ContractRow | null>(null);
  const [allowOpen, setAllowOpen] = useState(false);
  const [period, setPeriod] = useState<{ period_start: string; period_end: string; visits_used: number; visits_allowed: number; carried_over_visits: number; labour_minutes_used: number; labour_minutes_allowed: number; carried_over_minutes: number } | null>(null);

  const load = useCallback(async () => {
    const r = await supabase.from('contracts')
      .select('id,contract_number,name,status,client_id,property_id,offer_id,currency,billing_period,price,starts_on,ends_on,auto_renew,renewal_notice_days,included_visits,visits_consumed,sla_response_minutes,sla_resolution_minutes,notes,clients(name),properties(name),contract_properties(properties(name))')
      .is('deleted_at', null).order('created_at', { ascending: false });
    const list = (r.data as unknown as Contract[]) ?? [];
    setRows(list); setSel((cur) => cur ?? list[0]?.id ?? null); setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const current = rows.find((c) => c.id === sel) ?? null;
  useEffect(() => {
    if (!sel) { setPeriod(null); return; }
    supabase.from('contract_allowance_periods').select('period_start,period_end,visits_used,visits_allowed,carried_over_visits,labour_minutes_used,labour_minutes_allowed,carried_over_minutes')
      .eq('contract_id', sel).is('deleted_at', null).lte('period_start', new Date().toISOString().slice(0, 10)).order('period_start', { ascending: false }).limit(1).maybeSingle()
      .then((r) => setPeriod((r.data as any) ?? null));
  }, [sel]);

  return (
    <Layout header={<Topbar actions={<Button onClick={() => { setEditing(null); setFormOpen(true); }}>+ {t('contract.new')}</Button>} />}>
      <div className="px-7 pt-6"><h1 className="text-[30px] font-semibold tracking-[-0.02em]">{t('nav.contracts')}</h1></div>
      <div className="flex flex-wrap items-start gap-[18px] px-7 pb-7 pt-[18px]">
        <Card className="min-w-0 max-w-[460px] flex-1 overflow-hidden p-0" style={{ flexBasis: 380 }}>
          {loading ? <div className="p-6 text-sm text-muted">{t('common.loading')}</div>
            : rows.length === 0 ? <div className="p-6 text-sm text-muted">{t('common.noData')}</div>
            : rows.map((c) => (
              <button key={c.id} onClick={() => setSel(c.id)} className={cn('flex w-full flex-col gap-1.5 border-b border-divider p-4 text-left last:border-0', c.id === sel ? 'bg-[#F5F7FF] shadow-[inset_3px_0_0_var(--pv-accent)]' : 'hover:bg-bg')}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium"><span className="font-mono text-xs text-muted">{c.contract_number}</span> {c.name}</span>
                  <StatusPill tone={TONE[c.status] ?? 'open'}>{t('contract.st_' + c.status)}</StatusPill>
                </div>
                <span className="text-sm text-muted">{c.clients?.name ?? c.properties?.name ?? ''}</span>
              </button>
            ))}
        </Card>

        {current && (
          <Card className="min-w-0 flex-[2] p-6" style={{ flexBasis: 480 }}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <span className="font-mono text-xs text-muted">{current.contract_number}</span>
                <h2 className="text-[20px] font-semibold">{current.name}</h2>
                <span className="text-sm text-muted">{current.clients?.name ?? ''}</span>
              </div>
              <div className="flex items-center gap-2"><StatusPill tone={TONE[current.status] ?? 'open'}>{t('contract.st_' + current.status)}</StatusPill><Button variant="ghost" size="sm" onClick={() => setAllowOpen(true)}>{t('allow.params')}</Button><Button variant="secondary" size="sm" onClick={() => { setEditing(current); setFormOpen(true); }}>{t('form.edit')}</Button></div>
            </div>

            {period && (
              <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 rounded-card bg-bg px-4 py-3 text-sm">
                <span className="text-xs font-medium text-muted">{t('allow.currentPeriod')}: {fmtDate(period.period_start, i18n.language, timezone)} → {fmtDate(period.period_end, i18n.language, timezone)}</span>
                <span className="font-mono">{t('allow.visits')}: {period.visits_used}/{period.visits_allowed + period.carried_over_visits}</span>
                <span className="font-mono">{t('allow.minutes')}: {period.labour_minutes_used}/{period.labour_minutes_allowed + period.carried_over_minutes}</span>
              </div>
            )}

            <div className="mt-5 grid grid-cols-3 gap-x-6 gap-y-4 text-sm">
              {[
                [t('cat.billingPeriod'), current.billing_period ? t('cat.period_' + current.billing_period) : '—'],
                [t('contract.price'), current.price != null ? formatCurrency(current.price, current.currency ?? 'IDR', i18n.language) : '—'],
                [t('contract.term'), `${current.starts_on ? fmtDate(current.starts_on, i18n.language, timezone) : '—'} → ${current.ends_on ? fmtDate(current.ends_on, i18n.language, timezone) : '—'}`],
                [t('contract.slaResolution'), current.sla_resolution_minutes ? `${current.sla_resolution_minutes} min` : '—'],
                [t('contract.autoRenew'), current.auto_renew ? `${t('common.yes')}${current.renewal_notice_days ? ` · ${current.renewal_notice_days}d` : ''}` : t('common.no')],
                [t('contract.source'), current.offer_id ? t('nav.catalogue') : '—'],
              ].map(([k, v]) => <div key={k} className="flex flex-col gap-0.5"><span className="text-xs text-muted">{k}</span><span className="font-medium">{v}</span></div>)}
            </div>

            {current.included_visits != null && (
              <div className="mt-5 flex flex-col gap-1.5">
                <div className="flex items-center justify-between text-sm"><span className="text-muted">{t('contract.visits')}</span><span className="font-mono">{current.visits_consumed} / {current.included_visits}</span></div>
                <ProgressBar value={current.included_visits ? Math.min(100, (current.visits_consumed / current.included_visits) * 100) : 0} />
              </div>
            )}

            <div className="mt-5">
              <span className="text-xs font-medium text-muted">{t('contract.coveredProperties')}</span>
              <div className="mt-1.5 flex flex-wrap gap-2">
                {(current.contract_properties ?? []).map((cp, i) => cp.properties && <span key={i} className="rounded-pill border border-border bg-bg px-2.5 py-1 text-sm">{cp.properties.name}</span>)}
                {(current.contract_properties ?? []).length === 0 && <span className="text-sm text-muted">—</span>}
              </div>
            </div>
          </Card>
        )}
      </div>
      <ContractForm open={formOpen} onClose={() => setFormOpen(false)} onSaved={load} initial={editing} />
      {allowOpen && current && <AllowanceEditor kind="contract" id={current.id} name={current.name} onClose={() => setAllowOpen(false)} onSaved={load} />}
    </Layout>
  );
}
